import type { TransactionTimelineEntry } from '@pf/contracts';
import { Circle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toEpochMs } from '@/lib/clock';
import { formatDateTime, formatRelative, formatTime } from '@/lib/datetime';
import { TONE_CLASSES, timelineEventMeta } from '@/lib/states';
import { cn, shortId } from '@/lib/utils';
import { Code } from '@/components/ui/primitives';

/**
 * A transaction's history, rendered from the **event log** rather than from its current status.
 *
 * SAD §13 makes the hash-chained event store the record of truth and treats status as a
 * projection of it. This component honours that: it shows the events that actually happened, in
 * sequence order, with the gap between them — which is what makes "the slip arrived nine minutes
 * after mark-paid, one minute inside the deadline" visible at a glance instead of requiring
 * arithmetic on two timestamps.
 *
 * The `sequence` number is displayed because per-transaction ordering is a guarantee the system
 * makes (SAD §9), and an operator investigating an ordering incident needs to see it.
 */

/** Props for {@link StateTimeline}. */
export interface StateTimelineProps {
  /** The transaction's events, in any order — the component sorts by `sequence`. */
  events: readonly TransactionTimelineEntry[];
  /** Reference instant for relative labels. */
  at?: number | undefined;
  /** Compact rendering for side panels. */
  dense?: boolean | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** Render an event log as a vertical timeline. */
export function StateTimeline({
  events,
  at,
  dense = false,
  className,
}: StateTimelineProps): ReactNode {
  const { t } = useTranslation();
  const ordered = [...events].sort((a, b) => a.sequence - b.sequence);

  if (ordered.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        {t('domain.stateTimeline.empty')}
      </p>
    );
  }

  return (
    <ol className={cn('relative space-y-0', className)}>
      {ordered.map((event, index) => {
        const meta = timelineEventMeta(event.type);
        const label = meta ? t(meta.labelKey) : event.type;
        const tone = TONE_CLASSES[meta?.tone ?? 'idle'];
        const previous = ordered[index - 1];
        const gapMs = previous ? toEpochMs(event.created_at) - toEpochMs(previous.created_at) : 0;
        const last = index === ordered.length - 1;

        return (
          <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'z-10 mt-1 flex size-3 shrink-0 items-center justify-center rounded-full ring-4 ring-background',
                  tone.dot,
                )}
                aria-hidden="true"
              >
                <Circle className="size-0" />
              </span>
              {last ? null : <span className={cn('w-px flex-1', tone.rail)} aria-hidden="true" />}
            </div>

            <div className={cn('min-w-0 flex-1', dense ? 'space-y-0.5' : 'space-y-1')}>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className={cn('text-sm font-medium', tone.text)}>{label}</span>
                {event.actor ? (
                  <span className="rounded-full bg-accent px-1.5 py-0.5 text-2xs font-medium text-accent-foreground">
                    {t('domain.stateTimeline.byActor', { name: event.actor.label })}
                  </span>
                ) : null}
                <span className="text-2xs text-muted-foreground">
                  {t('domain.stateTimeline.sequenceLabel', { sequence: event.sequence })}
                  {gapMs > 1_000
                    ? ` ${t('domain.stateTimeline.gapSuffix', { gap: formatGap(gapMs) })}`
                    : null}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-muted-foreground">
                <time dateTime={event.created_at} title={formatDateTime(event.created_at)}>
                  {dense ? formatTime(event.created_at) : formatDateTime(event.created_at)}
                </time>
                <span aria-hidden="true">·</span>
                <span>{formatRelative(event.created_at, at)}</span>
                {dense ? null : (
                  <>
                    <span aria-hidden="true">·</span>
                    <Code className="bg-transparent px-0">{shortId(event.id)}</Code>
                  </>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Short "+9m 12s" style gap between two adjacent events. */
function formatGap(ms: number): string {
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
